/**
 * Avatar Utilities
 * Các hàm tiện ích để xử lý avatar: tạo màu, lấy chữ cái đầu, v.v.
 */

/**
 * Bảng màu cho avatar (Material Design inspired)
 * Các màu được chọn để có độ tương phản tốt với chữ trắng
 */
const AVATAR_COLORS = [
  "#FF6B9D", // Pink
  "#C44569", // Red
  "#5F27CD", // Purple
  "#00D2D3", // Cyan
  "#54A0FF", // Blue
  "#48DBFB", // Light Blue
  "#1DD1A1", // Green
  "#FFA502", // Orange
  "#EE5A6F", // Coral
  "#4834DF", // Indigo
  "#0FB9B1", // Teal
  "#FDA7DF", // Light Pink
] as const;

/**
 * Tạo màu ngẫu nhiên nhưng consistent cho một tên
 * Cùng một tên sẽ luôn có cùng một màu
 *
 * @param name - Tên người dùng
 * @returns Mã màu hex
 *
 * @example
 * getAvatarColor("Nguyễn Văn A") // "#FF6B9D"
 * getAvatarColor("Nguyễn Văn A") // "#FF6B9D" (luôn giống nhau)
 */
export const getAvatarColor = (name: string): string => {
  if (!name || name.trim().length === 0) {
    return AVATAR_COLORS[0];
  }

  // Tính hash từ tên để có màu consistent
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }

  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

/**
 * Lấy chữ cái đầu tiên của tên (viết hoa)
 * Nếu tên có nhiều từ, lấy chữ cái đầu của từ đầu tiên
 *
 * @param name - Tên người dùng
 * @returns Chữ cái đầu viết hoa
 *
 * @example
 * getInitials("Nguyễn Văn A") // "N"
 * getInitials("john doe") // "J"
 * getInitials("") // "?"
 */
export const getInitials = (name: string): string => {
  if (!name || name.trim().length === 0) {
    return "?";
  }

  const trimmedName = name.trim();
  return trimmedName.charAt(0).toUpperCase();
};

/**
 * Lấy initials từ họ và tên (2 chữ cái)
 * Hữu ích cho avatar hiển thị 2 chữ cái
 *
 * @param name - Tên đầy đủ
 * @returns 2 chữ cái đầu
 *
 * @example
 * getFullInitials("Nguyễn Văn A") // "NV"
 * getFullInitials("John Doe") // "JD"
 * getFullInitials("Single") // "SI"
 */
export const getFullInitials = (name: string): string => {
  if (!name || name.trim().length === 0) {
    return "??";
  }

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    // Nếu chỉ có 1 từ, lấy 2 chữ cái đầu
    return words[0].substring(0, 2).toUpperCase();
  }

  // Lấy chữ cái đầu của 2 từ đầu tiên
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

/**
 * Tạo style object cho Avatar component
 *
 * @param name - Tên người dùng
 * @param hasImage - Có ảnh hay không
 * @returns Style object với backgroundColor
 *
 * @example
 * getAvatarStyle("Nguyễn Văn A", false) // { backgroundColor: "#FF6B9D" }
 * getAvatarStyle("Nguyễn Văn A", true) // { backgroundColor: undefined }
 */
export const getAvatarStyle = (name: string, hasImage: boolean = false) => {
  return {
    backgroundColor: hasImage ? undefined : getAvatarColor(name),
  };
};

/**
 * Alias cho getInitials để tương thích với các component khác
 *
 * @param name - Tên người dùng
 * @returns Chữ cái đầu viết hoa
 */
export const getAvatarText = getInitials;

/**
 * Format thời gian thành dạng relative (tương đối)
 *
 * @param dateString - Chuỗi thời gian ISO hoặc timestamp
 * @returns Chuỗi thời gian tương đối (VD: "2 phút trước", "1 giờ trước")
 *
 * @example
 * formatRelativeTime("2024-12-14T10:53:00") // "2 phút trước"
 * formatRelativeTime("2024-12-14T09:00:00") // "1 giờ trước"
 */
export const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return "";

  // Ép UTC → VN time
  const date = new Date(dateString + "Z");
  const now = new Date();

  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "Vừa xong";

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} phút trước`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} giờ trước`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays} ngày trước`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) return `${diffInWeeks} tuần trước`;

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `${diffInMonths} tháng trước`;

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears} năm trước`;
};
/**
 * Format thời gian thành dạng DD/MM/YYYY
 *
 * @param dateString - Chuỗi thời gian ISO
 * @returns Chuỗi ngày tháng (VD: "18/12/2025")
 */
export const formatDate = (dateString: string | any): string => {
  if (!dateString) return "";
  const str = String(dateString);

  try {
    // 1. Thử regex cho DD-MM-YYYY hoặc DD/MM/YYYY trước (format hay gặp ở backend này)
    const dmyMatch = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmyMatch) {
      return `${dmyMatch[1].padStart(2, "0")}/${dmyMatch[2].padStart(2, "0")}/${dmyMatch[3]}`;
    }

    // 2. Thử parse qua Date (cho format ISO)
    let date = new Date(str.includes("Z") || str.includes("T") ? str : str + "Z");

    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    return str; // Trả về chuỗi gốc nếu không parse được
  } catch (error) {
    return str;
  }
};
